import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Card,
  Center,
  Group,
  Pagination,
  Select,
  SimpleGrid,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { IconChevronDown, IconChevronRight, IconSearch } from '@tabler/icons-react';
import { Fragment, useEffect, useState } from 'react';
import formatDate from '../../constants/formattedDate';
import prettifiedLabel from '../../constants/labelPrettifier';
import useFetch from '../../helpers/useFetch';
import ErrorMessage from '../ErrorMessage';
import Spinner from '../Spinner';
import classes from './SuperAdminBotClassification.module.css';

const SEARCH_DEBOUNCE_MS = 300;

const OUTCOMES = {
  classified: { label: 'Classified', color: 'green' },
  cookieLoss: { label: 'Skipped — cookie loss', color: 'yellow' },
  noEngagementSignal: { label: 'Skipped — no engagement signal', color: 'yellow' },
  failed: { label: 'Failed', color: 'red' },
};

// The endpoint's own bucket for domains with no verdict row at all, which the table renders
// through the same badge, so the label has to stay identical to the one OutcomeBadge prints.
const NEVER_CLASSIFIED = 'Never classified';

const OUTCOME_OPTIONS = [
  { value: '', label: 'All outcomes' },
  ...Object.entries(OUTCOMES).map(([value, { label }]) => ({ value, label })),
  { value: 'never', label: NEVER_CLASSIFIED },
];

// Keyed by the severity the endpoint derives from the verdict; it owns the banding, so nothing
// here recomputes it from the shares.
const SEVERITIES = {
  critical: { label: 'Critical', color: 'red' },
  high: { label: 'High', color: 'orange' },
  medium: { label: 'Medium', color: 'yellow' },
  unknown: { label: 'Unknown', color: 'gray' },
  none: { label: 'None', color: 'green' },
};

const SEVERITY_OPTIONS = [
  { value: '', label: 'All severities' },
  ...Object.entries(SEVERITIES).map(([value, { label }]) => ({ value, label })),
];

const SORT_OPTIONS = [
  { value: 'name', label: 'Name' },
  { value: 'severity', label: 'Severity' },
];

const SeverityBadge = ({ severity }) => {
  const known = SEVERITIES[severity];
  return (
    <Badge className={classes.outcomeBadge} variant="light" color={known ? known.color : 'gray'}>
      {known ? known.label : severity}
    </Badge>
  );
};

const missing = (value) => value === null || value === undefined;

const number = (value) => (missing(value) ? '-' : Number(value).toLocaleString());

const percent = (share) => (missing(share) ? '-' : `${(Number(share) * 100).toFixed(1)}%`);

const Metric = ({ label, value }) => (
  <Box>
    <Text size="xs" c="dimmed">
      {label}
    </Text>
    <Text size="sm">{value}</Text>
  </Box>
);

const OutcomeBadge = ({ run }) => {
  if (!run) {
    return (
      <Badge className={classes.outcomeBadge} variant="outline" color="gray">
        {NEVER_CLASSIFIED}
      </Badge>
    );
  }
  const outcome = OUTCOMES[run.outcome];
  return (
    <Badge className={classes.outcomeBadge} variant="light" color={outcome ? outcome.color : 'gray'}>
      {outcome ? outcome.label : run.outcome}
    </Badge>
  );
};

const Correction = ({ run }) => {
  if (!run) {
    return (
      <Text span size="sm" c="dimmed">
        -
      </Text>
    );
  }
  // activeFlagsBefore/activeFlags carry the correction only on a classified run;
  // a skip leaves them at 0, which would otherwise read as "no bots found".
  if (run.outcome !== 'classified') {
    return (
      <Text span size="sm" c="dimmed">
        Not measured
      </Text>
    );
  }
  const before = Number(run.activeFlagsBefore);
  const after = Number(run.activeFlags);
  const change = (100 * (after - before)) / before;
  return (
    <>
      {number(before)} → {number(after)}
      {before > 0 && <Text span size="xs" c="dimmed">{` (${change > 0 ? '+' : ''}${change.toFixed(1)}%)`}</Text>}
    </>
  );
};

const RunDetail = ({ run }) => (
  <SimpleGrid cols={{ base: 2, sm: 3, md: 6 }} spacing="md" verticalSpacing="sm">
    <Metric label="Window end" value={formatDate(run.windowEnd)} />
    <Metric label="Referrer coverage" value={percent(run.referrerCoverage)} />
    <Metric label="Internal referrer share" value={percent(run.internalReferrerShare)} />
    <Metric label="Chained to other profile share" value={percent(run.chainedToOtherProfileShare)} />
    <Metric label="Engaged share" value={percent(run.engagedShare)} />
    <Metric label="Internal referrer measured" value={run.internalReferrerMeasured ? 'Yes' : 'No'} />
    <Metric label="Chained measured" value={run.chainedMeasured ? 'Yes' : 'No'} />
    <Metric label="Active profiles" value={number(run.activeProfiles)} />
    <Metric label="Engaged profiles" value={number(run.engagedProfiles)} />
    <Metric label="Clean single-visit profiles" value={number(run.cleanSingleVisitProfiles)} />
    <Metric label="Referred single-visit profiles" value={number(run.referredSingleVisitProfiles)} />
    <Metric label="Internal referrer profiles" value={number(run.internalReferrerProfiles)} />
    <Metric label="External referrer profiles" value={number(run.externalReferrerProfiles)} />
    <Metric label="Small address page views" value={number(run.smallAddressPageViews)} />
    <Metric label="Chained page views" value={number(run.chainedPageViews)} />
    <Metric label="Chained to other profile" value={number(run.chainedToOtherProfile)} />
    <Metric label="Chained same profile" value={number(run.chainedSameProfile)} />
  </SimpleGrid>
);

const SuperAdminBotClassification = () => {
  const fetch = useFetch();
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [outcome, setOutcome] = useState('');
  const [severity, setSeverity] = useState('');
  const [sort, setSort] = useState('name');
  const [page, setPage] = useState(1);
  const [result, setResult] = useState();
  const [loadError, setLoadError] = useState();
  const [expandedId, setExpandedId] = useState();
  const [thresholdsOpen, setThresholdsOpen] = useState(false);

  // The endpoint searches across every domain, so a request per keystroke would be
  // one cross-platform scan each; debounce the way the operational email events tab does.
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    let cancelled = false;
    setResult(undefined);
    setLoadError(undefined);

    const params = new URLSearchParams({ page: String(page) });
    if (search) {
      params.set('search', search);
    }
    if (outcome) {
      params.set('outcome', outcome);
    }
    if (severity) {
      params.set('severity', severity);
    }
    if (sort !== 'name') {
      params.set('sort', sort);
    }

    fetch(`/api/admin/botClassification?${params.toString()}`)
      .then((r) => {
        if (!cancelled) setResult(r);
      })
      .catch((e) => {
        if (!cancelled) setLoadError(e);
      });

    return () => {
      cancelled = true;
    };
  }, [page, search, outcome, severity, sort, fetch]);

  const domains = result?.domains || [];
  const pagesCount = result ? Math.ceil(result.total / result.perPage) : 0;

  return (
    <Card withBorder>
      <Card.Section inheritPadding py="sm">
        <Text size="sm" c="dimmed" mb="sm">
          The latest nightly bot-classifier verdict per domain. Only a classified domain gets a bot-corrected MAU; a
          skipped or never-classified domain's MAU stays unadjusted.
        </Text>
        <Group align="flex-end" mb="sm" wrap="wrap">
          <TextInput
            value={searchInput}
            onChange={(event) => setSearchInput(event.currentTarget.value)}
            placeholder="Search by domain name or id"
            leftSection={<IconSearch size={14} />}
            style={{ flex: 1, minWidth: 220 }}
          />
          <Select
            label="Outcome"
            data={OUTCOME_OPTIONS}
            value={outcome}
            // Not debounced like the search field: this is a discrete choice, so waiting
            // would only delay a request the user has already finished making.
            onChange={(value) => {
              setOutcome(value || '');
              setPage(1);
            }}
            allowDeselect={false}
            w={240}
          />
          <Select
            label="Severity"
            data={SEVERITY_OPTIONS}
            value={severity}
            onChange={(value) => {
              setSeverity(value || '');
              setPage(1);
            }}
            allowDeselect={false}
            w={170}
          />
          <Select
            label="Sort by"
            data={SORT_OPTIONS}
            value={sort}
            onChange={(value) => {
              setSort(value || 'name');
              setPage(1);
            }}
            allowDeselect={false}
            w={150}
          />
        </Group>

        {result && (
          <Text size="sm" c="dimmed" mb="sm">
            {result.total.toLocaleString()} {result.total === 1 ? 'domain' : 'domains'}
          </Text>
        )}

        {result?.thresholds && (
          <>
            <Button variant="subtle" size="compact-sm" onClick={() => setThresholdsOpen(!thresholdsOpen)} mb="sm">
              {thresholdsOpen ? 'Hide thresholds' : 'Show thresholds'}
            </Button>
            {thresholdsOpen && (
              <SimpleGrid cols={{ base: 2, sm: 3, md: 6 }} spacing="md" verticalSpacing="sm" mb="md">
                {Object.entries(result.thresholds).map(([key, value]) => (
                  <Metric
                    key={key}
                    label={prettifiedLabel(key)}
                    value={typeof value === 'number' ? value.toLocaleString() : String(value)}
                  />
                ))}
              </SimpleGrid>
            )}
          </>
        )}

        {loadError ? (
          <ErrorMessage message={loadError.message || 'Failed to load bot classification runs'} />
        ) : !result ? (
          <Spinner />
        ) : domains.length === 0 ? (
          <Center py="xl">
            <Text c="dimmed">{search || outcome ? 'No domains match these filters.' : 'No domains to show.'}</Text>
          </Center>
        ) : (
          <>
            <Table.ScrollContainer minWidth={700}>
              <Table highlightOnHover className={classes.table}>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th />
                    <Table.Th className={classes.domain}>Domain</Table.Th>
                    <Table.Th>Outcome</Table.Th>
                    <Table.Th>Severity</Table.Th>
                    <Table.Th>Last run</Table.Th>
                    <Table.Th>Correction</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {domains.map(({ id, name, run, severity: rowSeverity }) => (
                    <Fragment key={id}>
                      <Table.Tr>
                        <Table.Td>
                          <ActionIcon
                            variant="subtle"
                            aria-label="Scoring detail"
                            aria-expanded={expandedId === id}
                            disabled={!run}
                            onClick={() => setExpandedId(expandedId === id ? undefined : id)}
                          >
                            {expandedId === id ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
                          </ActionIcon>
                        </Table.Td>
                        <Table.Td className={classes.domain}>
                          <Group gap="xs">
                            <Text span fw={700}>
                              {name}
                            </Text>
                            <Text span size="xs" c="dimmed">
                              #{id}
                            </Text>
                          </Group>
                        </Table.Td>
                        <Table.Td>
                          <OutcomeBadge run={run} />
                        </Table.Td>
                        <Table.Td>
                          <SeverityBadge severity={rowSeverity} />
                        </Table.Td>
                        <Table.Td>{run ? formatDate(run.runAt) : '-'}</Table.Td>
                        <Table.Td>
                          <Correction run={run} />
                        </Table.Td>
                      </Table.Tr>
                      {expandedId === id && (
                        <Table.Tr>
                          <Table.Td colSpan={6} className={classes.detail}>
                            <RunDetail run={run} />
                          </Table.Td>
                        </Table.Tr>
                      )}
                    </Fragment>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
            {pagesCount > 1 && (
              <Center mt="md">
                <Pagination total={pagesCount} value={page} onChange={setPage} />
              </Center>
            )}
          </>
        )}
      </Card.Section>
    </Card>
  );
};

export default SuperAdminBotClassification;
